import { CircleNotch } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getApiErrorMessage, useDeleteWifiPassword } from "@/lib/api";

interface Props {
  householdId: string;
  networkName?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation before clearing a household's stored Wi-Fi password.
 *
 * This is the button behind the promise the sharing page makes to the
 * household: their password is deleted once the setup is confirmed. Nothing
 * else clears the column, so the copy spells out both things people assume
 * wrongly. Only the password goes, and it does not come back.
 */
export function WifiPasswordDeleteDialog({ householdId, networkName, open, onOpenChange }: Props) {
  const deleteMutation = useDeleteWifiPassword();
  const [error, setError] = useState("");

  const handleDelete = async () => {
    setError("");
    try {
      await deleteMutation.mutateAsync({ id: householdId });
      onOpenChange(false);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to delete the Wi-Fi password."));
    }
  };

  // Clear any stale error whenever the dialog opens or closes, so a failed
  // delete doesn't leave its message behind when the dialog is reopened.
  const handleOpenChange = (nextOpen: boolean) => {
    setError("");
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Wi-Fi password</DialogTitle>
          <DialogDescription>
            Delete the stored Wi-Fi password for this household?{" "}
            {networkName ? (
              <>
                The network name <strong>{networkName}</strong> is kept.
              </>
            ) : (
              "The network name is kept."
            )}{" "}
            Only the password is removed, and this cannot be undone. To get it back, the household
            has to submit their details again through a new sharing link.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <CircleNotch className="h-4 w-4 animate-spin" weight="light" />
            ) : (
              "Delete password"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
